import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createProduct,
  updateProduct,
  fetchCategories,
  fetchProductById,
} from "../api/productApi";
import { toast } from "react-toastify";

/* ================= DEFAULT COLORS ================= */

const DEFAULT_COLORS = {
  Red: "#FF0000",
  Blue: "#0000FF",
  Black: "#000000",
  White: "#FFFFFF",
  Green: "#008000",
  Yellow: "#FFFF00",
  Orange: "#FFA500",
  Purple: "#800080",
  Pink: "#FFC0CB",
  Brown: "#A52A2A",
  Grey: "#808080",
  Navy: "#000080",
  Maroon: "#800000",
  Beige: "#F5F5DC",
  Cream: "#FFFDD0",
  Olive: "#808000",
  Teal: "#008080",
  Cyan: "#00FFFF",
  Magenta: "#FF00FF",
};

/* ================= COMPONENT ================= */

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isEdit = !!id;

  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    quantity: "",
    category: "",
    sizes: [],
    colors: [],
    isFeatured: false,
    photo: "",
  });

  const [categories, setCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  /* ================= FETCH DATA ================= */

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const categoriesData = await fetchCategories();
        setCategories(categoriesData);

        if (isEdit && id) {
          const product = await fetchProductById(id);

          const variants = product.variants || [];

          /* ================= SIZES ================= */

          const sizes = [
            ...new Set(
              variants
                .map((v) => v.size)
                .filter(Boolean)
            ),
          ];

          /* ================= COLORS ================= */

          const colorMap = {};

          variants.forEach((v) => {
            const name = v.color?.name;

            if (name && !colorMap[name]) {
              colorMap[name] = {
                name,
                hex:
                  v.color?.hex ||
                  DEFAULT_COLORS[name] ||
                  "#000000",
              };
            }
          });

          const colors = Object.values(colorMap);

          /* ================= SET FORM ================= */

          setForm({
            name: product.name || "",
            description: product.description || "",
            price: product.price || "",
            quantity: product.quantity || "",
            category: product.category?._id || "",
            isFeatured: product.isFeatured || false,
            sizes,
            colors,
            photo: "",
          });

          /* ================= IMAGE ================= */

          setImagePreview(product.photo || "");
        }
      } catch (err) {
        console.error("FETCH PRODUCT ERROR:", err);
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isEdit, id]);

  /* ================= HANDLE CHANGE ================= */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ================= SIZE ================= */

  const handleSizeChange = (size) => {
    setForm((prev) => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter((s) => s !== size)
        : [...prev.sizes, size],
    }));
  };

  /* ================= COLOR ================= */

  const handleColorChange = (colorName) => {
    setForm((prev) => {
      const exists = prev.colors.find(
        (color) => color.name === colorName
      );

      /* Remove color */
      if (exists) {
        return {
          ...prev,
          colors: prev.colors.filter(
            (color) => color.name !== colorName
          ),
        };
      }

      /* Add color */
      return {
        ...prev,
        colors: [
          ...prev.colors,
          {
            name: colorName,
            hex:
              DEFAULT_COLORS[colorName] ||
              "#000000",
          },
        ],
      };
    });
  };

  /* ================= HEX CHANGE ================= */

  const handleHexChange = (colorName, hex) => {
    setForm((prev) => ({
      ...prev,
      colors: prev.colors.map((color) =>
        color.name === colorName
          ? {
              ...color,
              hex: hex.toUpperCase(),
            }
          : color
      ),
    }));
  };

  /* ================= IMAGE ================= */

  const handleImage = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setForm((prev) => ({
      ...prev,
      photo: file,
    }));

    setImagePreview(URL.createObjectURL(file));
  };

  /* ================= BUILD VARIANTS ================= */

  const buildVariants = () => {
    const variants = [];

    form.sizes.forEach((size) => {
      form.colors.forEach((color) => {
        variants.push({
          size,
          color: {
            name: color.name,
            hex: color.hex,
          },
          stock: 10,
          sku: `${form.name}-${size}-${color.name}`
            .replace(/\s+/g, "-")
            .toLowerCase(),
        });
      });
    });

    return variants;
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error("Please enter product name");
      return;
    }

    if (!form.price) {
      toast.error("Please enter product price");
      return;
    }

    if (!form.quantity) {
      toast.error("Please enter product quantity");
      return;
    }

    if (!form.category) {
      toast.error("Please select a category");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("name", form.name);
      formData.append(
        "description",
        form.description
      );
      formData.append("price", form.price);
      formData.append("quantity", form.quantity);
      formData.append("category", form.category);
      formData.append(
        "isFeatured",
        form.isFeatured
      );

      /* ================= IMAGE ================= */

      if (form.photo) {
        formData.append("photo", form.photo);
      }

      /* ================= VARIANTS ================= */

      formData.append(
        "variants",
        JSON.stringify(buildVariants())
      );

      if (isEdit) {
        await updateProduct(id, formData);
        toast.success("Product updated successfully");
      } else {
        await createProduct(formData);
        toast.success("Product created successfully");
      }

      navigate("/products");
    } catch (err) {
      console.error(
        "SAVE PRODUCT ERROR:",
        err
      );

      toast.error(
        isEdit
          ? "Failed to update product"
          : "Failed to create product"
      );
    } finally {
      setSaving(false);
    }
  };

  /* ================= LOADING ================= */

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <p className="text-sm text-gray-500">
          Loading product...
        </p>
      </div>
    );
  }

  /* ================= UI ================= */

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* ================= PAGE HEADER ================= */}

      <div className="mb-5">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
          {isEdit
            ? "Update Product"
            : "Create Product"}
        </h1>

        <p className="text-sm text-gray-500 mt-1">
          {isEdit
            ? "Update your product information"
            : "Add a new product to your store"}
        </p>
      </div>

      {/* ================= FORM CARD ================= */}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <form
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* ================= BASIC INFORMATION ================= */}

          <section>
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">
              Basic Information
            </h2>

            <div className="space-y-4">
              {/* NAME */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Product Name
                </label>

                <input
                  name="name"
                  placeholder="Enter product name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* DESCRIPTION */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Description
                </label>

                <textarea
                  name="description"
                  placeholder="Enter product description"
                  value={form.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* PRICE + QUANTITY */}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Price
                  </label>

                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      ₹
                    </span>

                    <input
                      name="price"
                      type="number"
                      min="0"
                      placeholder="0"
                      value={form.price}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Quantity
                  </label>

                  <input
                    name="quantity"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={form.quantity}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* CATEGORY */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Category
                </label>

                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">
                    Select Category
                  </option>

                  {categories.map((c) => (
                    <option
                      key={c._id}
                      value={c._id}
                    >
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* ================= SIZES ================= */}

          <section className="border-t border-gray-100 pt-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800">
              Sizes
            </h2>

            <p className="text-xs sm:text-sm text-gray-500 mt-1 mb-4">
              Select the sizes available for this product.
            </p>

            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                "XS",
                "S",
                "M",
                "L",
                "XL",
                "XXL",
              ].map((size) => {
                const selected =
                  form.sizes.includes(size);

                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() =>
                      handleSizeChange(size)
                    }
                    className={`
                      py-2.5 rounded-lg border text-sm font-medium transition
                      ${
                        selected
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-gray-300 text-gray-700 hover:border-blue-400"
                      }
                    `}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
          </section>

          {/* ================= COLORS ================= */}

          <section className="border-t border-gray-100 pt-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800">
              Colors
            </h2>

            <p className="text-xs sm:text-sm text-gray-500 mt-1 mb-4">
              Select available colors and customize their HEX values.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.keys(DEFAULT_COLORS).map(
                (colorName) => {
                  const selectedColor =
                    form.colors.find(
                      (color) =>
                        color.name === colorName
                    );

                  const selected = !!selectedColor;

                  return (
                    <div
                      key={colorName}
                      className={`
                        border rounded-lg p-3 transition
                        ${
                          selected
                            ? "border-blue-400 bg-blue-50"
                            : "border-gray-200 bg-white"
                        }
                      `}
                    >
                      {/* Color Selection */}

                      <div className="flex items-center justify-between gap-2">
                        <label className="flex items-center gap-2 cursor-pointer min-w-0">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() =>
                              handleColorChange(
                                colorName
                              )
                            }
                            className="w-4 h-4 accent-blue-600"
                          />

                          <span className="text-sm font-medium text-gray-700">
                            {colorName}
                          </span>
                        </label>

                        {/* Color Preview */}

                        <span
                          className="w-6 h-6 rounded-full border border-gray-300 shrink-0"
                          style={{
                            backgroundColor:
                              selectedColor?.hex ||
                              DEFAULT_COLORS[
                                colorName
                              ],
                          }}
                        />
                      </div>

                      {/* Color Settings */}

                      {selectedColor && (
                        <div className="flex items-center gap-2 mt-3">
                          <input
                            type="color"
                            value={
                              selectedColor.hex ||
                              "#000000"
                            }
                            onChange={(e) =>
                              handleHexChange(
                                colorName,
                                e.target.value
                              )
                            }
                            className="w-10 h-9 p-0.5 border border-gray-300 rounded cursor-pointer shrink-0"
                          />

                          <input
                            type="text"
                            value={
                              selectedColor.hex || ""
                            }
                            onChange={(e) =>
                              handleHexChange(
                                colorName,
                                e.target.value
                              )
                            }
                            placeholder="#FF0000"
                            className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2.5 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </section>

          {/* ================= IMAGE ================= */}

          <section className="border-t border-gray-100 pt-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800">
              Product Image
            </h2>

            <p className="text-xs sm:text-sm text-gray-500 mt-1 mb-4">
              Upload the main product image.
            </p>

            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 sm:p-6">
              <input
                type="file"
                accept="image/*"
                onChange={handleImage}
                className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />

              {imagePreview && (
                <div className="mt-4">
                  <p className="text-xs text-gray-500 mb-2">
                    Image Preview
                  </p>

                  <div className="w-32 h-32 sm:w-40 sm:h-40 border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                    <img
                      src={imagePreview}
                      alt="Product preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ================= FEATURED ================= */}

          <section className="border-t border-gray-100 pt-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="isFeatured"
                checked={form.isFeatured}
                onChange={handleChange}
                className="w-4 h-4 accent-blue-600"
              />

              <div>
                <p className="text-sm font-medium text-gray-800">
                  Featured Product
                </p>

                <p className="text-xs text-gray-500">
                  Show this product as a featured item.
                </p>
              </div>
            </label>
          </section>

          {/* ================= ACTIONS ================= */}

          <div className="border-t border-gray-100 pt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate("/products")}
              disabled={saving}
              className="w-full sm:w-auto px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving
                ? isEdit
                  ? "Updating..."
                  : "Creating..."
                : isEdit
                ? "Update Product"
                : "Create Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;